import { X } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import type { SegmentationCondition, SegmentationFieldDefinition, SegmentationOperator } from '@/shared/types/segmentation';
import {
  OPERATOR_LABELS,
  OPERATORS_RELATIVE_DAYS,
  OPERATORS_WITHOUT_VALUE,
  OPERATORS_WITH_RANGE,
} from '../model/segmentation-operator-labels';
import { PUSH_BASE_FIELD } from '../model/push-base-filter';

interface Props {
  condition: SegmentationCondition;
  fieldCatalog: SegmentationFieldDefinition[];
  onChange: (patch: Partial<SegmentationCondition>) => void;
  onRemove: () => void;
}

export function SegmentationConditionRow({ condition, fieldCatalog, onChange, onRemove }: Props) {
  const fieldDef = fieldCatalog.find((f) => f.key === condition.field);
  const operators = fieldDef?.operators ?? [];
  const needsValue =
    fieldDef?.type !== 'boolean' && !OPERATORS_WITHOUT_VALUE.includes(condition.operator);
  const isRange = OPERATORS_WITH_RANGE.includes(condition.operator);
  const showPurchaseHint = condition.field === 'purchaseProduct' || condition.field === 'purchaseDate';
  // Push já é o filtro base fixo; só continua visível numa condição antiga que ainda o use.
  const selectableFields = fieldCatalog.filter(
    (field) => field.key !== PUSH_BASE_FIELD || field.key === condition.field,
  );

  function handleFieldChange(nextField: string) {
    const nextDef = fieldCatalog.find((f) => f.key === nextField);
    const nextOperator = (nextDef?.operators[0] ?? 'equals') as SegmentationOperator;
    onChange({ field: nextField, operator: nextOperator, value: undefined });
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={condition.field || undefined} onValueChange={handleFieldChange}>
          <SelectTrigger className="h-9 min-w-[160px] flex-1 text-xs sm:text-sm">
            <SelectValue placeholder="Selecione um campo" />
          </SelectTrigger>
          <SelectContent>
            {selectableFields.map((field) => (
              <SelectItem key={field.key} value={field.key}>
                {field.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={condition.operator}
          onValueChange={(value) => onChange({ operator: value as SegmentationOperator })}
          disabled={!fieldDef}
        >
          <SelectTrigger className="h-9 min-w-[140px] flex-1 text-xs sm:text-sm">
            <SelectValue placeholder="Operador" />
          </SelectTrigger>
          <SelectContent>
            {operators.map((op) => (
              <SelectItem key={op} value={op}>
                {OPERATOR_LABELS[op]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {needsValue ? (
          <ConditionValueInput condition={condition} fieldDef={fieldDef} isRange={isRange} onChange={onChange} />
        ) : null}

        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          aria-label="Remover condição"
        >
          <X className="size-3.5" />
        </Button>
      </div>
      {showPurchaseHint ? (
        <p className="text-[10px] leading-snug text-muted-foreground">
          Produto e data no mesmo card com E recortam a mesma compra.
        </p>
      ) : null}
    </div>
  );
}

interface ValueInputProps {
  condition: SegmentationCondition;
  fieldDef: SegmentationFieldDefinition | undefined;
  isRange: boolean;
  onChange: (patch: Partial<SegmentationCondition>) => void;
}

function ConditionValueInput({ condition, fieldDef, isRange, onChange }: ValueInputProps) {
  if (!fieldDef) return null;

  if (OPERATORS_RELATIVE_DAYS.includes(condition.operator)) {
    const raw = condition.value;
    const display =
      typeof raw === 'number' || (typeof raw === 'string' && /^\d+$/.test(raw)) ? String(raw) : '';
    return (
      <Input
        type="number"
        min={0}
        className="h-9 min-w-[140px] flex-1 text-xs sm:text-sm"
        placeholder="N dias"
        value={display}
        onChange={(e) => onChange({ value: e.target.value === '' ? undefined : Number(e.target.value) })}
      />
    );
  }

  if (isRange) {
    const [min, max] = Array.isArray(condition.value) ? condition.value : [undefined, undefined];
    const inputType = fieldDef.type === 'date' ? 'date' : 'number';
    return (
      <div className="flex flex-1 items-center gap-1.5">
        <Input
          type={inputType}
          className="h-9 text-xs sm:text-sm"
          placeholder="De"
          value={min ?? ''}
          onChange={(e) => onChange({ value: [e.target.value, max] })}
        />
        <span className="text-xs text-muted-foreground">e</span>
        <Input
          type={inputType}
          className="h-9 text-xs sm:text-sm"
          placeholder="Até"
          value={max ?? ''}
          onChange={(e) => onChange({ value: [min, e.target.value] })}
        />
      </div>
    );
  }

  if (fieldDef.options && fieldDef.options.length > 0) {
    const selected = condition.value == null || condition.value === '' ? undefined : String(condition.value);
    return (
      <Select value={selected} onValueChange={(value) => onChange({ value })}>
        <SelectTrigger className="h-9 min-w-[140px] flex-1 text-xs sm:text-sm">
          <SelectValue placeholder="Selecione…" />
        </SelectTrigger>
        <SelectContent>
          {fieldDef.options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (fieldDef.type === 'date') {
    return (
      <Input
        type="date"
        className="h-9 min-w-[140px] flex-1 text-xs sm:text-sm"
        value={(condition.value as string) ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
      />
    );
  }

  return (
    <Input
      type={fieldDef.type === 'number' ? 'number' : 'text'}
      className="h-9 min-w-[140px] flex-1 text-xs sm:text-sm"
      placeholder="Valor"
      value={(condition.value as string | number | undefined) ?? ''}
      onChange={(e) => onChange({ value: e.target.value })}
    />
  );
}
